
import React from 'react';
import ModernPolicyLayout from '@/components/legal/ModernPolicyLayout';

const TermsOfService = () => {
  const tableOfContents = [
    { id: 'acceptance', title: 'Acceptance of Terms', level: 1 },
    { id: 'license', title: 'Use License', level: 1 },
    { id: 'user-accounts', title: 'User Accounts', level: 1 },
    { id: 'payment-billing', title: 'Payment & Billing', level: 1 },
    { id: 'user-conduct', title: 'User Conduct', level: 1 },
    { id: 'beta-features', title: 'Beta Features', level: 1 },
    { id: 'third-party', title: 'Third-Party Services', level: 1 },
    { id: 'disclaimer', title: 'Service Disclaimer', level: 1 },
    { id: 'limitations', title: 'Liability Limitations', level: 1 },
    { id: 'accuracy', title: 'Accuracy of Materials', level: 1 },
    { id: 'calculator-disclaimer', title: 'Calculator Disclaimer', level: 1 },
    { id: 'modifications', title: 'Terms Modifications', level: 1 },
    { id: 'arbitration', title: 'Arbitration & Disputes', level: 1 },
    { id: 'service-availability', title: 'Service Availability', level: 1 },
    { id: 'governing-law', title: 'Governing Law', level: 1 },
    { id: 'contact-info', title: 'Contact Information', level: 1 }
  ];

  return (
    <ModernPolicyLayout 
      title="Terms of Service" 
      estimatedReadTime="15 min read"
      lastUpdated="Last updated: October 10, 2025"
      tableOfContents={tableOfContents}
    >
      <div className="space-y-8">
        <section id="acceptance">
          <h2>1. Acceptance of Terms</h2>
          <p>
            Welcome to EquiForge™ Homebuyer Optimizer. By accessing or using our mobile application, 
            you agree to be bound by these Terms of Service and all applicable laws and regulations. 
            If you do not agree with any of these terms, you are prohibited from using or accessing this app.
          </p>
        </section>

        <section id="license">
          <h2>2. Use License</h2>
          <p>
            Permission is granted to temporarily download one copy of the app for personal, 
            non-commercial transitory viewing only. This is the grant of a license, not a transfer 
            of title, and under this license you may not:
          </p>
          <ul>
            <li>Modify or copy the materials without explicit written permission</li>
            <li>Use the materials for any commercial purpose or for any public display (commercial or non-commercial)</li>
            <li>Attempt to decompile or reverse engineer any software contained in the app</li>
            <li>Remove any copyright or other proprietary notations from the materials</li>
            <li>Transfer the materials to another person or "mirror" the materials on any other server</li>
            <li>Use the app in any way that violates any applicable federal, state, local, or international law</li>
          </ul>
        </section>

        <section id="user-accounts">
          <h2>3. User Accounts</h2>
          <p>
            To access certain features of EquiForge™ Homebuyer Optimizer, you must create an account. 
            When you create an account, you agree to:
          </p>
          <ul>
            <li><strong>Accurate Information:</strong> Provide true, accurate, current, and complete information during registration</li>
            <li><strong>Security:</strong> Maintain the security of your password and account credentials</li>
            <li><strong>Account Responsibility:</strong> Accept responsibility for all activities that occur under your account</li>
            <li><strong>Notification:</strong> Notify us immediately of any unauthorized use of your account</li>
            <li><strong>Age Requirement:</strong> Confirm that you are at least 18 years of age and legally able to enter into contracts</li>
            <li><strong>Single User:</strong> Not share your account credentials or allow others to access your account</li>
          </ul>
          <p>
            We reserve the right to suspend or terminate accounts that violate these terms, provide 
            false information, or engage in fraudulent activity. Account termination may result in 
            the deletion of your data and loss of access to saved calculations and preferences.
          </p>
        </section>

        <section id="payment-billing">
          <h2>4. Payment & Billing Terms</h2>
          <p>
            EquiForge™ may offer both free and paid subscription tiers. By subscribing to a paid plan, 
            you agree to the following payment terms:
          </p>
          
          <h3>Subscription Plans</h3>
          <ul>
            <li><strong>Free Tier:</strong> Limited access to basic calculators and features</li>
            <li><strong>Premium Tier:</strong> Full access to all calculators, advanced analytics, and priority support</li>
            <li><strong>Professional Tier:</strong> Enterprise features, API access, and dedicated account management</li>
          </ul>

          <h3>Payment Processing</h3>
          <p>
            All payments are processed securely through Stripe, our third-party payment processor. 
            By providing payment information, you authorize us to charge your payment method for 
            the subscription fees and any applicable taxes. You represent that you have the legal 
            right to use any payment method you provide.
          </p>

          <h3>Automatic Renewal</h3>
          <p>
            Subscriptions automatically renew at the end of each billing period (monthly or annual) 
            unless you cancel before the renewal date. You will be charged the then-current subscription 
            rate. We will provide notice of any price increases at least 30 days before they take effect.
          </p>

          <h3>Cancellation & Refunds</h3>
          <p>
            You may cancel your subscription at any time through your account settings. Cancellations 
            take effect at the end of the current billing period. We do not offer refunds for partial 
            billing periods except as required by law or at our sole discretion.
          </p>

          <h3>Failed Payments</h3>
          <p>
            If a payment fails, we will attempt to charge your payment method up to three times over 
            a 10-day period. If payment continues to fail, your subscription will be downgraded to 
            the free tier, and you may lose access to premium features and saved data.
          </p>

          <h3>Taxes</h3>
          <p>
            Subscription fees do not include applicable sales, use, or value-added taxes. You are 
            responsible for any taxes imposed by your jurisdiction, which will be added to your invoice.
          </p>
        </section>

        <section id="user-conduct">
          <h2>5. User Conduct</h2>
          <p>
            You agree to use EquiForge™ Homebuyer Optimizer in compliance with all applicable laws 
            and regulations. Prohibited activities include, but are not limited to:
          </p>
          <ul>
            <li><strong>Illegal Activities:</strong> Using the app for any unlawful purpose or to violate any laws</li>
            <li><strong>Fraud:</strong> Providing false information or impersonating another person or entity</li>
            <li><strong>Unauthorized Access:</strong> Attempting to gain unauthorized access to our systems or other user accounts</li>
            <li><strong>Scraping & Automation:</strong> Using automated systems, bots, or scrapers to extract data from the app</li>
            <li><strong>Reverse Engineering:</strong> Attempting to decompile, disassemble, or reverse engineer the app</li>
            <li><strong>Interference:</strong> Disrupting, interfering with, or creating an undue burden on the app or its networks</li>
            <li><strong>Malicious Code:</strong> Uploading viruses, malware, or any malicious code</li>
            <li><strong>Abuse:</strong> Harassing, threatening, or abusing our staff or other users</li>
            <li><strong>Commercial Use:</strong> Reselling, redistributing, or commercially exploiting the app without authorization</li>
            <li><strong>Data Mining:</strong> Extracting data for commercial databases or competitive purposes</li>
          </ul>
          <p>
            Violation of these conduct rules may result in immediate account suspension or termination 
            without refund. We reserve the right to report illegal activities to law enforcement.
          </p>
        </section>

        <section id="beta-features">
          <h2>6. Beta Features & Early Access</h2>
          <p>
            EquiForge™ Homebuyer Optimizer may include beta, preview, or experimental features 
            ("Beta Features"). By using Beta Features, you acknowledge and agree that:
          </p>
          <ul>
            <li><strong>"As-Is" Basis:</strong> Beta Features are provided "as-is" without warranties of any kind</li>
            <li><strong>Reliability:</strong> Beta Features may be unstable, incomplete, or contain errors and bugs</li>
            <li><strong>Data Loss:</strong> Beta Features may result in data loss or corruption; back up your data regularly</li>
            <li><strong>Changes:</strong> Beta Features may be modified, discontinued, or removed at any time without notice</li>
            <li><strong>No SLA:</strong> Service level agreements do not apply to Beta Features</li>
            <li><strong>Feedback:</strong> You grant us a perpetual license to use any feedback you provide about Beta Features</li>
            <li><strong>Confidentiality:</strong> Some Beta Features may be confidential and should not be publicly disclosed</li>
          </ul>
          <p>
            We appreciate your participation in testing Beta Features and value your feedback to 
            improve EquiForge™. Please report bugs and issues to support@equiforge.com.
          </p>
        </section>

        <section id="third-party">
          <h2>7. Third-Party Services</h2>
          <p>
            EquiForge™ Homebuyer Optimizer integrates with several third-party services to provide 
            our functionality. Your use of the app is also subject to the terms and privacy policies 
            of these third-party providers:
          </p>

          <h3>Authentication Services</h3>
          <p>
            <strong>Supabase Auth:</strong> We use Supabase Authentication for user authentication and 
            identity management. By creating an account, you agree to Supabase's Terms of Service and 
            Privacy Policy. Supabase handles your login credentials, email verification, and session 
            management securely.
          </p>

          <h3>Payment Processing</h3>
          <p>
            <strong>Stripe:</strong> All payment processing is handled by Stripe, Inc. Your payment 
            information is never stored on our servers. Stripe is PCI DSS Level 1 certified and 
            complies with international payment security standards. See Stripe's Terms and Privacy 
            Policy for details.
          </p>

          <h3>Database & Backend</h3>
          <p>
            <strong>Supabase:</strong> Your data is stored and processed using Supabase infrastructure. 
            Supabase provides secure, scalable database services with encryption at rest and in transit. 
            Data is stored in SOC 2 Type II compliant data centers.
          </p>

          <h3>Error Monitoring</h3>
          <p>
            <strong>Sentry:</strong> We use Sentry for error tracking and performance monitoring. 
            Sentry may collect technical data about errors, crashes, and performance issues to help 
            us improve the app.
          </p>

          <h3>Third-Party Liability</h3>
          <p>
            EquiForge™ is not responsible for the availability, accuracy, or practices of third-party 
            services. Any issues with third-party services should be addressed directly with those 
            providers. We reserve the right to change third-party providers at any time with or 
            without notice.
          </p>
        </section>

        <section id="disclaimer">
          <h2>8. Service Disclaimer</h2>
          <p>
            The materials on EquiForge™ Homebuyer Optimizer are provided on an 'as is' basis. 
            EquiForge™ makes no warranties, expressed or implied, and hereby disclaims and negates 
            all other warranties including, without limitation, implied warranties or conditions of 
            merchantability, fitness for a particular purpose, or non-infringement of intellectual 
            property or other violation of rights.
          </p>
        </section>

        <section id="limitations">
          <h2>9. Liability Limitations</h2>
          <p>
            In no event shall EquiForge™ or its suppliers be liable for any damages (including, 
            without limitation, damages for loss of data or profit, or due to business interruption) 
            arising out of the use or inability to use the materials on EquiForge™ Homebuyer Optimizer, 
            even if EquiForge™ or a EquiForge™ authorized representative has been notified orally or 
            in writing of the possibility of such damage. Because some jurisdictions do not allow 
            limitations on implied warranties, or limitations of liability for consequential or 
            incidental damages, these limitations may not apply to you.
          </p>
        </section>

        <section id="accuracy">
          <h2>10. Accuracy of Materials</h2>
          <p>
            The materials appearing in EquiForge™ Homebuyer Optimizer could include technical, 
            typographical, or photographic errors. EquiForge™ does not warrant that any of the 
            materials on its app are accurate, complete or current. EquiForge™ may make changes 
            to the materials contained in its app at any time without notice. However EquiForge™ 
            does not make any commitment to update the materials.
          </p>
        </section>

        <section id="calculator-disclaimer">
          <h2>11. Financial Calculator Disclaimer</h2>
          <p>
            The mortgage calculators provided in this app are for informational and educational 
            purposes only and are not intended to provide specific financial, investment or tax advice. 
            The calculated results are estimates based on the information you provide and may not 
            reflect actual loans available to you.
          </p>
          <p>
            <strong>Important:</strong> You should consult with qualified professionals such as loan officers, 
            mortgage brokers, financial advisors, accountants, or attorneys for specific advice on 
            mortgage products and your individual financial situation. EquiForge™ does not guarantee 
            loan approval or specific terms based on calculator results.
          </p>
        </section>

        <section id="modifications">
          <h2>12. Terms Modifications</h2>
          <p>
            EquiForge™ may revise these terms of service for its app at any time without notice. 
            By using this app you are agreeing to be bound by the then current version of these 
            terms of service. Material changes to these terms will be communicated via email or 
            in-app notification at least 30 days prior to taking effect.
          </p>
        </section>

        <section id="arbitration">
          <h2>13. Arbitration & Dispute Resolution</h2>
          
          <h3>Agreement to Arbitrate</h3>
          <p>
            You and EquiForge™ agree to resolve any disputes, claims, or controversies arising from 
            or relating to these Terms or the app through binding arbitration, except as specified below. 
            This agreement to arbitrate is intended to be broadly interpreted and includes claims based 
            on contract, tort, statute, fraud, misrepresentation, or any other legal theory.
          </p>

          <h3>Arbitration Procedures</h3>
          <p>
            Arbitration will be conducted by the American Arbitration Association (AAA) under its 
            Commercial Arbitration Rules and Supplementary Procedures for Consumer Related Disputes. 
            The arbitration will be held in San Francisco, California, or another mutually agreed location. 
            The arbitrator's decision will be binding and may be entered as a judgment in any court of 
            competent jurisdiction.
          </p>

          <h3>Costs of Arbitration</h3>
          <p>
            Payment of all filing, administration, and arbitrator fees will be governed by the AAA Rules. 
            If your claim is for less than $10,000, EquiForge™ will reimburse you for filing fees and 
            arbitrator's fees if you prevail in the arbitration.
          </p>

          <h3>Exceptions to Arbitration</h3>
          <p>
            Either party may bring a claim in small claims court if the claim qualifies. Additionally, 
            either party may seek injunctive or other equitable relief in court to prevent the actual 
            or threatened infringement, misappropriation, or violation of intellectual property rights.
          </p>

          <h3>Class Action Waiver</h3>
          <p>
            <strong>YOU AND EQUIFORGE™ AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN 
            YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED 
            CLASS OR REPRESENTATIVE PROCEEDING.</strong> Unless both you and EquiForge™ agree, no 
            arbitrator or judge may consolidate more than one person's claims or otherwise preside 
            over any form of a representative or class proceeding.
          </p>

          <h3>Opt-Out Right</h3>
          <p>
            You have the right to opt out of this arbitration agreement by sending written notice of 
            your decision to opt out to legal@equiforge.com within 30 days of first accepting these 
            Terms. Your opt-out notice must include your name, address, and a clear statement that 
            you wish to opt out of arbitration.
          </p>
        </section>

        <section id="service-availability">
          <h2>14. Service Availability</h2>
          <p>
            EquiForge™ strives to provide reliable service but makes no guarantees regarding availability 
            or uptime. We reserve the right to:
          </p>
          <ul>
            <li><strong>Maintenance:</strong> Perform scheduled and emergency maintenance that may result in service interruptions</li>
            <li><strong>Modifications:</strong> Modify, suspend, or discontinue any aspect of the app at any time</li>
            <li><strong>Feature Changes:</strong> Add, remove, or modify features without prior notice</li>
            <li><strong>Access Restrictions:</strong> Limit access to certain features based on subscription tier or other factors</li>
            <li><strong>Geographic Limitations:</strong> Restrict access from certain geographic locations</li>
            <li><strong>Capacity Management:</strong> Implement usage limits to maintain service quality</li>
          </ul>
          
          <h3>Force Majeure</h3>
          <p>
            EquiForge™ shall not be liable for any failure to perform due to circumstances beyond its 
            reasonable control, including but not limited to acts of God, war, terrorism, riots, natural 
            disasters, power failures, internet outages, strikes, or government actions.
          </p>

          <h3>Service Termination</h3>
          <p>
            EquiForge™ reserves the right to discontinue the service entirely with 90 days notice. 
            In the event of service termination, you will have the opportunity to export your data 
            during the notice period. Prepaid subscription fees for service after the termination date 
            will be refunded on a pro-rata basis.
          </p>
        </section>

        <section id="governing-law">
          <h2>15. Governing Law</h2>
          <p>
            These terms and conditions are governed by and construed in accordance with the laws 
            of the United States and the State of California, and you irrevocably submit to the 
            exclusive jurisdiction of the courts in that state or location.
          </p>
        </section>

        <section id="contact-info">
          <h2>16. Contact Information</h2>
          <p>
            For any questions about these Terms of Service, please contact us at: 
            <strong> support@equiforge.com</strong>
          </p>
          <p>
            For legal matters specifically, contact: <strong>legal@equiforge.com</strong>
          </p>
        </section>
      </div>
    </ModernPolicyLayout>
  );
};

export default TermsOfService;
